/**
 * The steward App Packs' ROLE facets.
 *
 * - `STEWARD_APP_ROLE_DEFS` — the steward-app pack: the four family/advisor roles plus
 *   one small role per sharing pillar.
 * - `STEWARD_ADVISOR_FIRM_ROLE_DEFS` — the steward-advisor-firm pack: four roles, the
 *   declared holders of `engagement:activate` (steward-app#1021), `engagement:read`
 *   (crossmarc/appcore#501) with `onboarding:request` (steward-app#1426),
 *   `firmroster:write` (crossmarc/appcore#645), and `engagement:provision`
 *   (steward-app#1481). Read both docblocks at the bottom of this file before touching
 *   anything about scopes — firm authority's took a reversal on 2026-09-09.
 *
 * These lived as an inline literal in `scripts/seed-steward-app.ts` until
 * steward-app#739. They moved here for the same reason `STEWARD_APP_TYPE_DEFS` lives
 * here: the pack's definition is data, the seed script is a writer, and the definition
 * has to be **testable**. The seed script cannot be imported by a test — it throws on a
 * missing `TABLE_NAME` at module scope and calls `main()` on load — so while the
 * permissions lived there, "the family roles hold every pillar capability" was a rule
 * with no mechanism to fail. That is the rule this whole phase rests on.
 *
 * The move also removes a duplicated role-id table: the seed exported its own
 * `STEWARD_APP_ROLE_IDS` while `roles.ts` exported `STEWARD_ROLE_IDS` with the same four
 * values, hand-maintained, in a repo whose own `roles.ts` header records a security bug
 * caused by exactly that ("two hand-maintained copies of a list have no mechanism to
 * disagree loudly"). There is now one.
 *
 * ## Storage shape is NOT here
 *
 * These are pure definitions. `pk`/`sk`/`_et`/`_v`/`_ua`/`_ub` are stamped by
 * `scripts/lib/packSeed.ts` at write time. Keeping DynamoDB keys out of the definition
 * is what lets this file be imported by the SPA and the façade without either learning
 * the table layout.
 */

import { ASSISTANT_READ_CAPABILITY } from './assistantAccess.js';
import {
  DOCUMENTS_SHARED_FOLDER_ROLE_ID,
  PEOPLE_REVEAL_TAX_ID_CAPABILITY, PEOPLE_TAX_ID_REVEAL_ROLE_ID,
  PILLAR_CAPABILITIES, SHARING_PILLARS,
} from './pillars.js';
import { STEWARD_ADVISOR_FIRM_ROLE_IDS, STEWARD_ROLE_IDS } from './roles.js';
import { FIRM_NW_WRITE_CAPABILITY } from './firmNetWorthAccess.js';
import { CLIENT_RECORD_WRITE_CAPABILITIES } from './clientRecordAccess.js';
import {
  CASH_FLOW_AGREEMENT_CAPABILITY, CASH_FLOW_CURSOR_WRITE_CAPABILITY, CASH_FLOW_READ_CAPABILITY,
  CASH_FLOW_ROLE_IDS, CASH_FLOW_TRANSACTIONS_CAPABILITY,
} from './cashFlowAccess.js';

/** A pack role definition, as seeded into `APP#<packId>` / `ROLE#<roleId>`. */
export interface PackRoleDef {
  readonly roleId: string;
  readonly code: string;
  readonly name: string;
  /**
   * ⚠️ **This MUST be `'workspace'` for every role in both packs**, or appcore's
   * `assertAssignableRoles` (`packages/core/src/auth/permissions.ts`) refuses to let the
   * invite path assign it. `'system'` here — not even a legal scope — is how the advisor
   * invite silently 400s.
   *
   * The field used to be narrowed to the literal `'workspace'` in this repo, because every
   * role shipped so far had to be assignable. appcore's own type
   * (`packages/core/src/db/pack-repository.ts` `PackRoleDef`) has always accepted the
   * three-way union, so the narrowing was ours, not the platform's.
   *
   * steward-app#1021 widened it because `steward-firm-authority` wanted the REFUSAL:
   * `assertAssignableRoles` rejects any role whose scope is not `'workspace'` on all four
   * self-service sites (membership create, membership updateRoles, invite create, invite
   * accept), so a `'tenant'`-scoped role could not be put onto a membership by any caller
   * through any API. **steward-app#1343 reversed that on 2026-09-09** and firm authority is
   * `'workspace'` like everything else; the union stays because narrowing a public type back
   * is a breaking change for no gain, and because the next role that wants the refusal
   * should not have to re-widen it.
   *
   * So: **there is currently no role in either pack for which a non-`'workspace'` scope is
   * anything but a bug that presents as a 400 the SPA cannot explain.**
   * `packRoles.test.ts` asserts the exception list is EMPTY, in both packs, so a
   * non-workspace scope cannot arrive unnoticed — adding one is a deliberate edit to that
   * test with a reason written beside it.
   */
  readonly scope: 'workspace' | 'tenant' | 'platform';
  readonly description: string;
  readonly permissions: readonly string[];
  readonly builtIn: true;
  /** `_ca` for a first write. Preserved from the stored row on every re-seed. */
  readonly createdAt: string;
  /**
   * ⚠️ **WRITE CONFINEMENT (crossmarc/appcore#553, appcore docs/06 §18).** When `true`, a
   * caller holding this role writes ONLY where an ACL entry names them (their
   * `USR#<userId>` or one of their `ROLE#<roleId>`s) and that entry carries the confinement
   * write verb (`FOLDER_CONTRIBUTE_VERB`, `folderCustody.ts`). Tier-3's opt-in fallback —
   * "a target with zero entries is unrestricted" — does not apply to them on the write
   * intent. Reads are untouched on every path.
   *
   * **It reaches the stored row by ordinary means, verified at source 2026-09-02.**
   * `scripts/seed-steward-app.ts` spreads every field of this definition except `createdAt`
   * onto the ROLE item, `scripts/lib/packSeed.ts`'s `upsertRole` writes that item verbatim
   * (and `definitionFingerprint` includes the flag, so flipping it re-seeds rather than
   * reporting `unchanged`), appcore's `RoleSchema` declares `writeConfined` as an optional
   * boolean, and `loadRole` (`packages/core/src/auth/permissions.ts`) reads a pack ROLE row
   * with a plain `as Role` cast and hands it to `resolveWriteConfinement`. Nothing on that
   * path strips or validates it away.
   *
   * ⚠️ **A confined role may never carry `mem:manage`, and the REASON is not the one this
   * comment used to give.** It said the role would be "confined in name only" because
   * `access.ts` cleared on break-glass before any Tier-3 evaluation. appcore#603 removed
   * `mem:manage` from that short-circuit, and the invariant did not move with it: the real
   * reason, which #603 left untouched, is that a `mem:manage` holder can `grantAcl` itself
   * the very ACL entries the confinement withholds. appcore states this on
   * `CONFINEMENT_FORBIDDEN_CAPABILITY` and enforces it in `assertConfinedRoleNotAdmin`
   * (renamed from `assertConfinedRoleNotBreakGlass` by #603) — but that runs in
   * `PackRepository.upsertRole`, and THIS repo's seed writes the row straight to DynamoDB,
   * so that runtime guard never sees it. `advisorContributor.test.ts` is our copy of the
   * invariant, using appcore's own `matchCapability` expansion so `mem:*` is caught as well
   * as the literal — which is what appcore does here too, and deliberately NOT what its
   * exact-match `hasUnconditionalCapability` does. Do not unify the two.
   *
   * ⚠️ **Confinement is a UNION over the membership and is the SECONDARY control.** Any
   * confined role on a membership confines the whole membership (so composing a pillar role
   * cannot clear it), but it reaches only the types appcore's `screen()` builds a graph for
   * — those declaring a `readVerb` or a `parent`. For every other type the PRIMARY control
   * is a `writeCapability` withheld from the confined role: `STEWARD_WRITE_CAPABILITY` on every
   * such type but one, and on `onboarding-request` a string no role holds (steward-app#1426). Do not relax either half; appcore docs/06 §18.4 states the
   * split, and `stewardWriteCapability.test.ts` is the census.
   */
  readonly writeConfined?: boolean;
  /**
   * ⚠️ **READ CONFINEMENT (crossmarc/appcore#786, appcore docs/06 §18.7).** For each capability
   * string listed, a caller holding this role reads a chain declaring that string as its
   * `readCapability` ONLY where an ACL entry names them at `read`: no opt-in fallback, no
   * ACL-administrator exemption, nothing rootless, and a capability-only type is refused
   * outright. It never grants the capability; `permissions` still has to.
   *
   * It reaches the row the same way `writeConfined` does (spread verbatim by
   * `scripts/seed-steward-app.ts`, included in `definitionFingerprint`). As with
   * `writeConfined`, appcore's own guards on it (the grammar check and
   * `assertConfinedRoleNotAdmin`) live in `PackRepository.upsertRole`, which this repo's seed
   * does NOT use, so `advisorContributor.test.ts` carries our copy of the no-`mem:manage`
   * invariant for these roles too.
   *
   * ⚠️ **Fails OPEN on an appcore without #786**, and **fails open if the row cannot be
   * loaded** (appcore docs/06 §18.7): the one confined role today is
   * {@link DOCUMENTS_SHARED_FOLDER_ROLE_ID}; read its docblock in `pillars.ts`.
   */
  readonly readConfinedCapabilities?: readonly string[];
}

const SEED_DATE = '2026-06-04T00:00:00Z';
/** The date the pillar roles were first minted (steward-app#739 phase 1). */
const PILLAR_SEED_DATE = '2026-08-24T00:00:00Z';
/** The date the tax-identification reveal role was first minted (steward-app#1260). */
const TAX_ID_REVEAL_SEED_DATE = '2026-09-15T00:00:00Z';
/** The date the write-confined advisor contributor role was first minted (steward-app#742 step 4). */
const CONTRIBUTOR_SEED_DATE = '2026-09-02T00:00:00Z';
/** The date the Documents-off shared-folder role was first minted (steward-app#1950). */
const DOCUMENTS_SHARED_FOLDER_SEED_DATE = '2026-09-30T00:00:00Z';
/** steward-app#2581: the four cash flow roles (`cashFlowAccess.ts`). */
const CASH_FLOW_SEED_DATE = '2026-10-26T00:00:00Z';

/**
 * appcore's mirror of `readCapability` on the write intent (crossmarc/appcore#449,
 * `AccessControlConfigSchema.writeCapability`). steward-app#742 phase 4b is the feature
 * this exists for — an advisor contributor role that can write into exactly one folder —
 * but that feature is **blocked on crossmarc/appcore#533**: appcore's Tier-3 folder ACLs
 * are opt-in restrictions, so a role holding `inst:write` and no folder-scoped confinement
 * can create, edit and delete any document the family has not individually restricted,
 * including the Unfiled folder. Until #533's per-membership write confinement lands,
 * nothing in this pack may grant an advisor `inst:write`.
 *
 * **GRANTED AND ARMED.** `STEWARD_WRITE_CAPABILITY` is in the `permissions` of the ADMIN
 * and MEMBER family roles below (steward-app#742 step 1 / #1127), exactly as
 * `credential:read` and the sharing pillars were granted ahead of enforcement (see the
 * docblock on `STEWARD_APP_ROLE_DEFS`), and steward-app#742 step 3 declares it as
 * `accessControl.writeCapability` on **every CTYPE in both packs except `document`,
 * `document-folder` and `onboarding-request`** (`index.ts`; the census is
 * `stewardWriteCapability.test.ts`). The third declares a string no role holds instead, so
 * that no family role can write it on the generic plane (steward-app#1426).
 *
 * ⚠️ This paragraph used to read "**This PR is the grant half only** ... no CTYPE declares
 * it anywhere in this repo". That is no longer true, and the practical consequence is the
 * one that paragraph was warning about in advance: **removing this string from ADMIN or
 * MEMBER is now a live write outage** for every holder of that role, on 18 types, not a
 * latent gap. It is in the same class as the pillar grants and `assistant:read`, not in the
 * class of a grant awaiting its arm.
 *
 * The parts of #742 that genuinely remain: the contributor role (step 4), the connect
 * change, and the copy update (docs/26 §4b amendment). appcore#533's per-membership write
 * confinement landed as appcore#553.
 *
 * ## Why ADMIN and MEMBER, and not VIEWER
 *
 * appcore#742's original text says "grant to admin/member/viewer"; that predates the
 * roles' current shapes. A `writeCapability` can only ever SHRINK the set of principals
 * who pass a write gate (appcore's stated doctrine, `pillars.ts` repeats it for the read
 * axis) — it is checked in addition to `inst:write`, never instead of it. `steward-viewer`
 * holds `inst:read` and no `inst:write` (see its `permissions` below), so it fails every
 * write gate regardless of any `writeCapability` string. Granting `steward:write` to
 * VIEWER would be meaningless in practice and misleading to read: a reviewer scanning the
 * role's permission list would see a write capability on a role that cannot write.
 * `steward-advisor`, the four pillar roles and `steward-firm-authority` are excluded for
 * the same reason — none holds `inst:write` — and additionally because none is a family
 * role this capability is meant to describe.
 */
export const STEWARD_WRITE_CAPABILITY = 'steward:write';

/**
 * The four base roles.
 *
 * FIVE capability families here were granted AHEAD of enforcement, and every one of them
 * is load-bearing. (This sentence said "two", then "three", then "four", above a list that
 * has grown once per phase; corrected each time in review — most recently steward-app#1260,
 * 2026-09-15.)
 *
 * - `credential:read` (appcore#411) — armed later by steward-app#643 on
 *   `credential`/`credential-vault`. Done; kept because a re-seed rewrites the row.
 * - `PILLAR_CAPABILITIES` (steward-app#739) — granted in phase 1, **armed by phase 2b** on
 *   every type `SHARING_PILLARS` names, EXCEPT any pillar still listed in
 *   `PILLAR_GRANTED_NOT_YET_ARMED`, which is granted here and armed a deploy later. **That
 *   list is empty on 2026-09-14, so all five strings are live gates**; `people` was its last
 *   occupant and steward-app#1260 phase 2 armed `person`. Kept for the same reason as the
 *   line above, and the armed ones are now the only thing keeping the family's own data
 *   readable to a steward MEMBER or VIEWER — which, with nothing exempt, means removing ANY
 *   of the five from a FAMILY role below is a LIVE lockout rather than a latent one. Spread
 *   into all three FAMILY roles below and deliberately NOT into
 *   `steward-advisor`. ⚠️ **The spread stays TOTAL over the table, never narrowed to the
 *   live set** — granting is the whole of phase 1, and narrowing it would mean the arm
 *   deploy finds the family roles without the string.
 * - `ASSISTANT_READ_CAPABILITY` (steward-app#932) — granted here in phase 1, **armed by
 *   phase 2** on `chat-session`, which now declares
 *   `accessControl.readCapability: 'assistant:read'`. So this string gates a real read:
 *   removing it from a FAMILY role below is a LIVE lockout of that role's holders from
 *   their own assistant history, not a latent one. `assistantAccess.test.ts` pins the
 *   grant and the arm, each in both directions. A fixed exclusion like `credential:read`,
 *   NOT a client-choosable pillar — see `assistantAccess.ts` for why a family's own
 *   transcripts must not become a toggle.
 * - `STEWARD_WRITE_CAPABILITY` (steward-app#742) — granted here to ADMIN and MEMBER only
 *   (see that constant's docblock for why not VIEWER), and **armed by step 3** on every
 *   CTYPE in both packs except `document`, `document-folder`, `onboarding-request` (which
 *   declares a string nobody holds, steward-app#1426) and, since steward-app#590, the
 *   creator-owned `chat-session` (`CREATOR_OWNED_TYPE_CODES`, `assistantAccess.ts`), where
 *   appcore's creator ownership is the write rule and refuses a `writeCapability` beside it.
 *   So this string now gates
 *   every write those roles make: removing it from ADMIN or MEMBER below is a LIVE outage,
 *   exactly like `assistant:read` and the pillar capabilities, not a latent one.
 * - `PEOPLE_REVEAL_TAX_ID_CAPABILITY` (steward-app#1260) — granted here to ADMIN and MEMBER
 *   only, and **now enforced at both ends**. This block said "not enforced by anything in THIS
 *   repo yet: no façade route asks for it" and that was true on 2026-09-15; PR 3 (#1454, merged
 *   `89695b5f`) mounted the three tax-id routes in `router.ts`, so it is false as of 2026-09-16.
 *   Corrected rather than deleted because the stale form was false in the dangerous direction:
 *   read literally it says this grant gates nothing here, which is an argument for tidying it
 *   away. Removing it from ADMIN or MEMBER is now a LIVE outage, like `STEWARD_WRITE_CAPABILITY`
 *   above, not a latent one.
 *
 *   The far end is appcore's reveal route (crossmarc/appcore#668), which checks this capability
 *   FIRST: `requirePermission(ctx, REVEAL_CAPABILITY)` precedes `requireStepUp`, the quota, the
 *   row read and `assertEntityAccessible` (read at `origin/main` 2026-09-16). So a holder who
 *   lacks it is refused for lacking it, never with a step-up challenge — which is what lets a
 *   test tell the capability gate apart from a floored workspace.
 *
 *   The seed-before-enforce ordering that this block originally recorded is **discharged for
 *   this capability and still binds the next one**: the grant has to be on the seeded roles in
 *   an environment before a caller can satisfy the route.
 *
 *   ⚠️ **NOT a member of `PILLAR_CAPABILITIES`, and it must never become one.** It is a
 *   ROUTE requirement declared on no CTYPE. Folding it into the pillar spread would grant it
 *   to VIEWER (which `pillars.test.ts`'s `role %s holds EVERY pillar capability` requires of
 *   every pillar string) and would drag it into the arm machinery, where the only honest
 *   expression of it would be a `readCapability` on `person` withholding the whole person
 *   record from anyone lacking it. See `pillars.ts` for both halves.
 *
 *   VIEWER is omitted on `credential:reveal`'s precedent, which is the closest analogue in
 *   the pack: a read-only family member sees that a number is on file and cannot decrypt it.
 *   ADVISOR is omitted on the pillar argument — what a firm may reveal is composed per
 *   connection as an extra roleId (`SHARING_PILLARS[].additionalRoleIds`), never granted to
 *   every firm forever.
 *
 * None of the five is covered by `inst:*`: appcore's `matchCapability` expands a wildcard only
 * within its own resource segment, so `inst:*` says nothing about `portfolio:read`. Every
 * string a gate will ask for has to be spelled out. Do NOT "tidy" these lists.
 */
export const STEWARD_APP_ROLE_DEFS: readonly PackRoleDef[] = [
  {
    roleId: STEWARD_ROLE_IDS.ADMIN,
    code: 'steward-admin', name: 'Admin', scope: 'workspace',
    description: 'Full control of the family office portal',
    // `credential:reveal` is a dedicated capability (NOT covered by `inst:*`) so only
    // admins/members can decrypt vault secrets; viewers with `inst:read` cannot.
    //
    // ⚠️ **The grants below are this role's ONLY reach, and this comment used to say the
    // opposite.** It called them "belt and braces", because a steward ADMIN also holds
    // `mem:manage` and `screen()` cleared on that before any capability check. appcore#603
    // narrowed `UNCONDITIONAL_CAPABILITIES` to `['*:*']` and #605 deployed it (verified at
    // source in the crossmarc/appcore checkout at `0d18c78`, 2026-09-08), so removing a
    // string that some type ARMS — `credential:read`, `assistant:read`, `steward:write`,
    // any `PILLAR_CAPABILITIES` member — is a LIVE lockout for an admin, exactly as it
    // already was for MEMBER below. (The rest of the list is ordinary platform authority
    // and behaves as it always did; the change is confined to the armed strings.) That is
    // the reading that matters, and it is the reverse of the one a "belt and braces" note
    // invites.
    //
    // Read the retirement narrowly. `mem:manage` still exempts its holder from Tier-3 ACL
    // EXCLUSION (appcore's `ACL_ADMINISTRATOR_CAPABILITY`: you cannot be locked out of an
    // ACL you administer), so an admin does still read an ACL-restricted folder without
    // being named on it. What it no longer does is satisfy a type-level `readCapability` /
    // `writeCapability` — the first of the three limits that constant states.
    //
    // The old note's secondary reason still stands on its own: `GET /w/{ws}/me/capabilities`
    // answers from these permissions, so a string missing here reads `false` at the probe
    // as well as at the gate (appcore `entities/role.ts` records the same trap for
    // `credential:read`). The two now agree, which is what steward-app#1254 relied on when
    // it dropped the SPA assistant hook's `mem:manage` fallback.
    permissions: [
      'ws:manage', 'mem:manage', 'inst:*', 'credential:reveal', 'credential:read',
      'type:read', 'type:write', 'audit:read', 'audit:manage',
      ASSISTANT_READ_CAPABILITY,
      STEWARD_WRITE_CAPABILITY,
      ...PILLAR_CAPABILITIES,
      // Spelled separately from the pillar spread, deliberately: it is a ROUTE capability
      // declared on no type, and VIEWER below does NOT hold it. See its own bullet above.
      PEOPLE_REVEAL_TAX_ID_CAPABILITY,
      // The cash flow agreement only (steward-app#2685), so an admin can turn cash flow on,
      // set levels and turn it off whatever their own level. NOT `cash-flow:read` or
      // `cash-flow:transactions`: those stay off every base role so "Nothing" stays possible
      // (`cashFlowAccess.ts`; `cashFlowAccess.test.ts` pins both halves).
      CASH_FLOW_AGREEMENT_CAPABILITY,
    ],
    builtIn: true, createdAt: SEED_DATE,
  },
  {
    roleId: STEWARD_ROLE_IDS.MEMBER,
    code: 'steward-member', name: 'Member', scope: 'workspace',
    description: 'Read and write portal data; cannot manage members',
    // The pillar grants are the ONLY thing keeping the family's own data readable to a
    // steward MEMBER now that phase 2b has armed the declarations, so removing one from
    // this list is a live lockout, not a latent one. That was once specific to this role
    // (`mem:read`, not `mem:manage`); since appcore#603 it is equally true of ADMIN above,
    // and the note is kept here rather than deleted because this is where it was first
    // learned.
    permissions: [
      'ws:read', 'mem:read', 'inst:read', 'inst:write', 'credential:reveal',
      'credential:read', 'type:read',
      ASSISTANT_READ_CAPABILITY,
      STEWARD_WRITE_CAPABILITY,
      ...PILLAR_CAPABILITIES,
      // As on ADMIN above, and for the same reason VIEWER below is left without it.
      PEOPLE_REVEAL_TAX_ID_CAPABILITY,
    ],
    builtIn: true, createdAt: SEED_DATE,
  },
  {
    roleId: STEWARD_ROLE_IDS.VIEWER,
    code: 'steward-viewer', name: 'Viewer', scope: 'workspace',
    description: 'Read-only access to portal data',
    // credential:read: existing read-only members keep credential visibility (confirmed
    // with Matt) -- this is about advisors gaining access, not viewers losing it. The
    // pillar grants below carry the identical intent one axis over.
    //
    // Deliberately NOT `STEWARD_WRITE_CAPABILITY` (steward-app#742 prep). This role holds
    // no `inst:write`, so a write capability here would pass no write gate it does not
    // already fail -- see that constant's docblock for the full reasoning.
    //
    // ⚠️ Deliberately NOT `PEOPLE_REVEAL_TAX_ID_CAPABILITY` either (steward-app#1260), and
    // the absence is the decision. This role DOES hold `people:read` through
    // `PILLAR_CAPABILITIES`, so a viewer sees the person record and the masked hint and
    // cannot decrypt the number — exactly the split `credential:read` without
    // `credential:reveal` already makes two lines up. It will read oddly the first time
    // someone hits it (a family's own read-only member cannot reveal, while a connected
    // advisor whose membership carries the composed reveal role can), and it is the founder's
    // call to overrule, not a session's. `pillars.test.ts` pins the absence as explicitly as
    // it pins the presence on ADMIN and MEMBER.
    permissions: [
      'ws:read', 'mem:read', 'inst:read', 'type:read', 'credential:read',
      ASSISTANT_READ_CAPABILITY,
      ...PILLAR_CAPABILITIES,
    ],
    builtIn: true, createdAt: SEED_DATE,
  },
  {
    roleId: STEWARD_ROLE_IDS.ADVISOR,
    code: 'steward-advisor', name: 'Advisor', scope: 'workspace',
    description: 'Read-only access to portal data for a connected advisor firm; excludes credentials',
    // Identical to steward-viewer EXCEPT `credential:read` is deliberately omitted --
    // this role IS defined by that omission (steward-app#643). The `credential` and
    // `credential-vault` CTYPEs now declare `readCapability: 'credential:read'`
    // (packages/steward-types/src/index.ts), so a role without this permission is
    // refused by appcore's shared read gate regardless of `inst:read`. Do NOT "tidy"
    // this list to match steward-viewer/steward-member -- that would silently
    // re-grant advisors credential visibility and undo the entire feature.
    //
    // steward-app#739 extends the SAME omission to the four pillar capabilities: this
    // role must hold NONE of them. What a firm can see is composed onto the membership
    // as extra `roleIds` (`SHARING_PILLARS[].roleId`), chosen per connection by the
    // family. Adding a pillar capability here would grant it to every firm, forever,
    // with no way to withdraw it.
    //
    // steward-app#932 makes `assistant:read` the SECOND fixed omission, alongside
    // `credential:read` and unlike the four pillars: a family's own conversations with
    // the assistant are withheld from every firm unconditionally, and that must never
    // become a client toggle (`assistantAccess.ts` says why). ARMED since phase 2, so the
    // omission bites: `chat-session` declares the string, and appcore's read gate refuses
    // this role the type regardless of `inst:read`. Adding it here would undo #932 as
    // completely as adding `credential:read` would undo #643.
    //
    // steward-app#1260 adds `people:reveal-tax-id` to the same list of strings this role must
    // NOT hold, on the pillar argument rather than the fixed-rule one: a firm may be granted
    // the tax-identification reveal PER CONNECTION, as the composed
    // `steward-share-people-tax-id` role (`SHARING_PILLARS[].additionalRoleIds`). Putting the
    // string here would grant the most sensitive field in the product to every firm that ever
    // connects, forever, with no way for the family to withdraw it.
    //
    // `scope: 'workspace'` (not 'system') is required or appcore's
    // assertAssignableRoles refuses to let the invite path assign it.
    permissions: ['ws:read', 'mem:read', 'inst:read', 'type:read'],
    builtIn: true, createdAt: SEED_DATE,
  },
  /**
   * ⚠️ **THE CONTRIBUTOR — the only role in either pack that holds `inst:write` without
   * holding `STEWARD_WRITE_CAPABILITY`, and that omission is the whole feature**
   * (steward-app#742 step 4).
   *
   * Composed onto an advisor membership beside `steward-advisor` and the pillar roles when
   * the family opts in, so that the firm can put documents INTO the folder the family
   * shares with them. Never granted alone and never to anyone else.
   *
   * ## What actually confines it, in the order the gates run
   *
   * 1. **`writeCapability: STEWARD_WRITE_CAPABILITY` on every other type — the PRIMARY
   *    control.** appcore's `screen()` short-circuits to ALLOW for a type declaring neither
   *    `readVerb` nor `parent` (`confinementCovers`, appcore `services/platform-api/src/
   *    access.ts`), so confinement reaches only four of this pack's types. What closes the
   *    rest is that they declare the capability and this role does not hold it
   *    (`onboarding-request` declares one NO role holds, steward-app#1426). **Do not
   *    add `STEWARD_WRITE_CAPABILITY` to the list below**, and do not remove the declaration
   *    from a type: either one hands this role the whole workspace, and neither would fail a
   *    test that only checked confinement. appcore docs/06 §18.4 says this in as many words.
   * 2. **`writeConfined: true` — the secondary control, on the document pair.** `document`
   *    and `document-folder` are the two types deliberately NOT capability-gated, because
   *    they are the surface this role exists to write. Confinement is what stops the write
   *    from being every document in the vault: a confined caller writes only where an ACL
   *    entry names them at the confinement write verb, which for this role is exactly one
   *    entry — the firm's `USR` entry on its own shared folder, written by
   *    `handlers/advisor.ts`' reconcile.
   * 3. **`custodyVerb: DOCUMENT_CUSTODY_VERB` on the document pair (step 2), withheld from
   *    that same entry.** Confinement gates destroy at the SAME verb as an edit (appcore
   *    docs/06 §18.3), so without this the family's ACL entry would license the firm to
   *    delete the family's documents and to delete the shared folder itself — appcore
   *    docs/06 §18.5 item 4 names that exact hazard. The custody tier is what closes it, and
   *    `writeGrants(..., custody: 'withhold', ...)` is what keeps the advisor's entry from
   *    ever carrying it.
   *
   * ## What it holds, and what supplies the rest
   *
   * `inst:read` mirrors `steward-advisor`'s so that the role is not silently narrower than
   * the membership it joins; `inst:write` is the capability appcore's write gates ask for
   * first. **It holds no `documents:read`, and that is deliberate**: both document types
   * declare `readCapability: 'documents:read'`, and appcore requires every `readCapability`
   * in the chain on the WRITE intent too (`requiredCapabilities`, `access.ts`). So a
   * contributor can only write documents where the membership's OTHER roles supply the read,
   * exactly as the composition supplies every other disclosure domain. Since steward-app#1950
   * that read comes from either documents role: the Documents area role, or, with Documents
   * off, the read-confined shared-folder role (`DOCUMENTS_SHARED_FOLDER_ROLE_ID`), which
   * reads only folders whose ACL names the firm. So the write now deliberately survives
   * Documents being turned off, confined to the shared folder on both sides. Granting
   * `documents:read` HERE would still be wrong: it is unconfined, so it would widen a
   * Documents-off firm's read to the whole vault. Withdrawing the contributor itself is the
   * opt-in's own switch, and disconnect strips it. `advisorContributor.test.ts` asserts the
   * omission.
   *
   * No `ws:read`/`mem:read`/`type:read`: like the pillar roles, this one is additive by
   * construction and means nothing on a membership that does not already carry
   * `steward-advisor`.
   *
   * ## Why the `mem:manage` omission is a real invariant here
   *
   * This section used to be headed "Break-glass" and gave TWO reasons: that `access.ts`
   * cleared on `mem:manage` before any Tier-3 evaluation, and that its holder can call
   * `grantAcl` and write themselves the very entry the confinement asks for. **The first
   * reason is retired; the second is untouched and is the whole invariant now.**
   *
   * appcore#603/#605 narrowed `UNCONDITIONAL_CAPABILITIES` to `['*:*']`, so `mem:manage` no
   * longer short-circuits anything ahead of a capability or ACL check (the coordinator read
   * this at appcore's deployed commit `0d18c788` on 2026-09-09 —
   * `packages/core/src/auth/permissions.ts:170` — and reported it live in dev, stage and
   * prod; not a scan this file's author ran). Confinement was never one of the gates that
   * clearing reached in any case: appcore suppresses the Tier-3 ACL-administrator exemption
   * inside the confined pass, so confinement wins there. `roles.ts`' `writeConfined`
   * docblock states the same correction.
   *
   * **Do not read the retirement as making this omission optional.** A confined role that
   * carried `mem:manage` could `grantAcl` itself the exact ACL entries the confinement
   * withholds, which is the reason appcore states on `CONFINEMENT_FORBIDDEN_CAPABILITY` and
   * enforces in `assertConfinedRoleNotAdmin` (renamed from `assertConfinedRoleNotBreakGlass`
   * by #603 — the rule was renamed, not removed). appcore refuses to persist such a role,
   * but only through `PackRepository.upsertRole`, which this repo's seed does not use: it
   * writes the row straight to DynamoDB. `advisorContributor.test.ts` is our copy of the
   * invariant, and relaxing its `mem:manage` assertion on the strength of #603 would be
   * relaxing the half of the argument #603 never touched.
   */
  {
    roleId: STEWARD_ROLE_IDS.ADVISOR_CONTRIBUTOR,
    code: 'steward-advisor-contributor', name: 'Advisor contributor', scope: 'workspace',
    description:
      'Lets a connected advisory firm add and update documents in the folder the family '
      + 'shares with them, and nowhere else. Composed onto the firm\'s membership beside the '
      + 'Advisor role only when the family asks for it.',
    permissions: ['inst:read', 'inst:write'],
    writeConfined: true,
    builtIn: true, createdAt: CONTRIBUTOR_SEED_DATE,
  },
  /**
   * One role per sharing pillar, holding ONLY that pillar's capability.
   *
   * Additive by construction: they carry no `ws:read`/`inst:read` of their own, so one
   * of these alone gives a principal nothing. They are only meaningful unioned onto a
   * membership that already carries `steward-advisor`, which is exactly the composition
   * phase 2a performs — and it means a bug that assigns one in isolation grants no access
   * rather than a surprising slice of it.
   *
   * Generated from `SHARING_PILLARS` rather than hand-written so a fifth pillar cannot
   * be added to the table without its role appearing in the seed.
   */
  ...SHARING_PILLARS.map((pillar): PackRoleDef => ({
    roleId: pillar.roleId,
    code: `steward-share-${pillar.id}`,
    name: `Shared area: ${pillar.label}`,
    scope: 'workspace',
    description: `Lets a connected advisor firm see the ${pillar.label} area, and nothing else. Composed onto the firm's membership alongside the Advisor role when the family chooses to share it.`,
    permissions: [pillar.capability],
    builtIn: true,
    createdAt: PILLAR_SEED_DATE,
  })),
  /**
   * The `people` pillar's SECOND composable role (steward-app#1260, 2026-09-15): the
   * tax-identification reveal, holding `people:reveal-tax-id` and nothing else.
   *
   * ⚠️ **Hand-written rather than generated, and that is not an oversight.** The map above
   * derives `permissions: [pillar.capability]` from the table, and `SharingPillar` carries
   * only role IDs in `additionalRoleIds` — no capability, no code, no label — so there is
   * nothing for a generated def to derive a second role's permission set from. Widening the
   * table to carry them would put a route-gated capability into the same structure the arm
   * assertions read, which is precisely what `pillars.ts` argues against. `pillars.test.ts`
   * closes the gap the hand-writing opens: every id in `PILLAR_ROLE_IDS` must resolve to a
   * seeded def, hold no write verb, and carry `scope: 'workspace'`, so a future additional
   * role added to the table without a def here reds rather than failing at seed time.
   *
   * Additive by construction like the five above: no `ws:read`/`inst:read` of its own, so
   * holding this alone grants nothing at all. It is only meaningful unioned onto a membership
   * that already carries `steward-advisor` (for a firm) — and since steward-app#1260 PR 4
   * (2026-09-15) the Family area's switch COMPOSES it, on `connect` and on any
   * `PUT /advisor/shared-areas` save that names that area, so real advisor memberships carry
   * it. (This paragraph
   * said the opposite, "NOTHING composes it onto any membership as of this commit", until the
   * commit that first composed one.) The role was nevertheless seeded a release EARLIER, in
   * PR 2 of 4 (#1450), so that it could be read back at source before `areaRoleIds` ever named
   * it: appcore's `assertAssignableRoles` refuses an unseeded roleId, and
   * `updateMember`/`issueInvite` write the whole `roleIds` array, so composing one that is not
   * there fails the entire membership write rather than dropping the id. That ordering is
   * discharged for this role and still binds the next one.
   *
   * `code` is `steward-share-people-tax-id`, a sibling of the generated
   * `steward-share-people`. `name` and `description` avoid the word "reveal" on its own
   * because they are read in appcore's own role lists: they say what a holder can do. The
   * description deliberately makes NO claim about auditing — the reveal route audits before
   * returning plaintext by design (crossmarc/appcore#668), but that is a property of another
   * repo's deployed code and a seeded string here cannot go red if it stops being true.
   */
  {
    roleId: PEOPLE_TAX_ID_REVEAL_ROLE_ID,
    code: 'steward-share-people-tax-id',
    name: 'Tax identification reveal (Family)',
    scope: 'workspace',
    description:
      'Lets a connected advisor firm see a family member\'s tax identification number, '
      + 'and nothing else. Composed onto the firm\'s membership alongside the Advisor role '
      + 'and the Family area role.',
    permissions: [PEOPLE_REVEAL_TAX_ID_CAPABILITY],
    builtIn: true,
    createdAt: TAX_ID_REVEAL_SEED_DATE,
  },
  /**
   * The `documents` pillar's OFF role (steward-app#1950, 2026-09-30): what a firm holds when
   * the family has Documents switched off, so that they keep the "Shared with «firm»" folder
   * and nothing else. Read `SharingPillar.offRoleIds` in `pillars.ts` first.
   *
   * `documents:read`, read-confined to that same string. Hand-written for the reason the tax-id
   * role above is: `offRoleIds` carries ids only. Additive like the rest of this family (no
   * `ws:read`/`inst:read`), and it holds no write verb: contributing to the folder is the
   * contributor role's job, and appcore requires `documents:read` under the write intent too,
   * so the two compose.
   *
   * ⚠️ Same pack as the documents pillar role, and never deleted while a membership may name
   * it: an unloadable confining role fails OPEN (appcore docs/06 §18.7).
   */
  {
    roleId: DOCUMENTS_SHARED_FOLDER_ROLE_ID,
    code: 'steward-share-documents-shared-folder',
    name: 'Shared folder only (Documents)',
    scope: 'workspace',
    description:
      'Lets a connected advisor firm see the folder the family shares with them, and no other '
      + 'documents. Composed onto the firm\'s membership when the family has the Documents area '
      + 'switched off.',
    permissions: ['documents:read'],
    readConfinedCapabilities: ['documents:read'],
    builtIn: true,
    createdAt: DOCUMENTS_SHARED_FOLDER_SEED_DATE,
  },
  /**
   * ⚠️ **The four cash flow roles (steward-app#2581, docs/39 §8.5). Read `cashFlowAccess.ts`
   * first.** Two composable FAMILY viewer roles and two composable FIRM roles, each holding
   * only cash flow strings. Hand-written, and NOT generated from `SHARING_PILLARS`: a pillar's
   * capability is spread into every base family role (the `PILLAR_CAPABILITIES` spread above,
   * pinned by `pillars.test.ts`), and no base role may hold either cash flow capability, so
   * the firm roles are standalone like the two hand-written roles above rather than pillars.
   *
   * Additive by construction, like every composable role here: no `ws:read`/`inst:read` of
   * their own and no write verb, so holding one alone grants nothing but its cash flow reads
   * (its level's string plus `cash-flow:agreement`, steward-app#2685).
   * A family viewer role is composed beside a base family role; a firm role beside
   * `steward-advisor`. The firm pair is composed by `PUT /advisor/shared-areas` (slice 10, `firmCashFlowRoleIds`).
   *
   * They exist before anything names them for the reason the tax-id role's docblock gives:
   * appcore's `assertAssignableRoles` refuses an unseeded roleId and a membership write sends
   * the whole `roleIds` array, so the role has to be seeded in an environment first.
   */
  {
    roleId: CASH_FLOW_ROLE_IDS.FAMILY_TOTALS,
    code: 'steward-cash-flow-totals',
    name: 'Cash flow: totals',
    scope: 'workspace',
    description:
      'Lets a family member see cash flow totals by month, entity and category, and no '
      + 'single transaction. Set per person by a family admin.',
    permissions: [CASH_FLOW_READ_CAPABILITY, CASH_FLOW_AGREEMENT_CAPABILITY],
    builtIn: true,
    createdAt: CASH_FLOW_SEED_DATE,
  },
  {
    roleId: CASH_FLOW_ROLE_IDS.FAMILY_DETAIL,
    code: 'steward-cash-flow-detail',
    name: 'Cash flow: every transaction',
    scope: 'workspace',
    description:
      'Lets a family member see cash flow totals and every transaction in the accounts the '
      + 'family includes. Set per person by a family admin.',
    // Both read strings, so "every transaction" implies totals with one role (docs/39 §8.5).
    permissions: [CASH_FLOW_READ_CAPABILITY, CASH_FLOW_TRANSACTIONS_CAPABILITY, CASH_FLOW_AGREEMENT_CAPABILITY],
    builtIn: true,
    createdAt: CASH_FLOW_SEED_DATE,
  },
  {
    roleId: CASH_FLOW_ROLE_IDS.FIRM_TOTALS,
    code: 'steward-share-cash-flow',
    name: 'Shared: Cash flow totals',
    scope: 'workspace',
    description:
      'Lets a connected advisor firm see the family\'s cash flow totals by month, entity and '
      + 'category, and nothing else. Composed onto the firm\'s membership alongside the Advisor '
      + 'role when the family chooses to share it.',
    permissions: [CASH_FLOW_READ_CAPABILITY, CASH_FLOW_AGREEMENT_CAPABILITY],
    builtIn: true,
    createdAt: CASH_FLOW_SEED_DATE,
  },
  {
    roleId: CASH_FLOW_ROLE_IDS.FIRM_DETAIL,
    code: 'steward-share-cash-flow-detail',
    name: 'Shared: Cash flow transactions',
    scope: 'workspace',
    description:
      'Lets a connected advisor firm see every transaction in the family\'s cash flow, and '
      + 'nothing else. Composed alongside the Advisor role and the cash flow totals role when '
      + 'the family chooses to share every transaction.',
    // `cash-flow:transactions` and NOT `cash-flow:read`. docs/39 §8.5 has the façade compose
    // the totals role beside this one (slice 10) rather than folding totals in, so the firm's
    // two levels stay two independent ids on the membership. The agreement string is on every
    // cash flow role (steward-app#2685).
    permissions: [CASH_FLOW_TRANSACTIONS_CAPABILITY, CASH_FLOW_AGREEMENT_CAPABILITY],
    builtIn: true,
    createdAt: CASH_FLOW_SEED_DATE,
  },
];

/**
 * The capability appcore's engagement gate asks for, spelled here as a literal.
 *
 * appcore owns the string (`ENGAGEMENT_ACTIVATE_CAPABILITY`,
 * `packages/core/src/db/engagement-repository.ts`) and this package deliberately does not
 * depend on `@appcore/core` — the same reason `pillars.test.ts` hand-copies appcore's
 * capability grammar. Keep it in sync by hand; a typo grants nothing and refuses
 * everything (the gate fails closed), so the failure mode is an activation that never
 * succeeds, not one that succeeds wrongly.
 *
 * ⚠️ **It is in its own `engagement:` namespace on purpose.** appcore's `matchCapability`
 * expands a verb wildcard within a resource segment, so `tenant:engage` / `ws:engage` /
 * `mem:engage` would be swallowed by `tenant-admin`'s `tenant:*` / `ws:*` / `mem:*` and
 * handed to every bootstrapped founder for free. Do not "tidy" this into an existing
 * namespace.
 */
export const ENGAGEMENT_ACTIVATE_CAPABILITY = 'engagement:activate';

/**
 * The capability appcore's ROSTER READ asks for (crossmarc/appcore#501), spelled here as a
 * literal for the same reason `ENGAGEMENT_ACTIVATE_CAPABILITY` above is.
 *
 * ⚠️ **A DISTINCT string from `engagement:activate`, not a weaker spelling of it.**
 * `GET /tenants/{tenantId}/engagements` returns every client workspace id a firm serves plus
 * each engagement's `floorPolicy` and `payerRef` — the confidential part of the advisory data
 * model. Before #501 that route gated on tenant MEMBERSHIP alone, so a contractor holding a
 * viewer membership in any workspace of the firm's tenant could enumerate the whole client
 * list. Reusing `engagement:activate` to close it was rejected in #501: the read must stay
 * available to ordinary firm staff whose role legitimately cannot BIND a client.
 *
 * The two are unordered — neither implies the other. A binder who also needs to read holds
 * BOTH roles.
 *
 * Same namespace reasoning as `engagement:activate`: `matchCapability` expands a verb
 * wildcard inside a resource segment, so `tenant:read` / `ws:read` / `mem:read` would be
 * swallowed by `tenant-admin`'s `tenant:*` / `ws:*` / `mem:*` and handed to every
 * bootstrapped founder for free. `engagement:` has no wildcard holder anywhere but
 * `platform-admin`'s `*:*`.
 */
export const ENGAGEMENT_READ_CAPABILITY = 'engagement:read';

/**
 * The capability appcore's member-plane ROSTER WRITES ask for (crossmarc/appcore#645),
 * spelled here as a literal for the same reason the two above are. appcore declares the same
 * string as `FIRM_ROSTER_WRITE_CAPABILITY` in `packages/core/src/db/advisor-firm-repository.ts`
 * (read at appcore `1157ad9`, 2026-09-10); the two repos share no package, so a rename on one
 * side is a silent disarm on the other, and `packRoles.test.ts` pins the literal.
 *
 * Its own `firmroster:` namespace for the reason `engagement:` has one: `matchCapability`
 * expands a verb wildcard inside a resource segment, so `ws:roster` / `mem:roster` would be
 * swallowed by `workspace-admin`'s and `tenant-admin`'s `ws:*` / `mem:*` and handed to every
 * workspace founder for free. `firmroster:` has no wildcard holder anywhere but
 * `platform-admin`'s `*:*`.
 *
 * The roster READ is not this string: appcore reads the roster for any holder of this OR of
 * `mem:read`, so staff can see unlisted colleagues without being able to change anything.
 */
export const FIRM_ROSTER_WRITE_CAPABILITY = 'firmroster:write';

/**
 * The firm capability appcore's ENGAGEMENT-AUTHORIZED WRITE asks for on the
 * `onboarding-request` type (crossmarc/appcore#679, steward-app#1426). The type declares it as
 * `engagementWrite.firmCapability` (`index.ts`); appcore reads that declaration off the PACK
 * row and resolves the string from the caller's FIRM-tenant memberships only
 * (`mayWriteForEngagedFirm` → `resolveFirmCapability`,
 * `packages/core/src/db/engagement-repository.ts`, read at appcore `19caa7c` on 2026-09-16).
 *
 * Unlike the three strings above, appcore does not own this literal: the pack picks it, and
 * appcore mints no consumer names. So there is nothing on the appcore side to keep in sync;
 * the declaration on the type and the grant on the role are the two halves, both in this
 * package. `packRoles.test.ts` pins the literal and its one holder, and
 * `onboardingRequest.test.ts` pins that the type's declaration names this constant.
 *
 * ⚠️ **A NEW string, deliberately not `engagement:activate`.** Reusing activation would make
 * every principal who may bind a household also the principal who may send the request, and
 * would put a second write behind crossmarc/appcore#642's self-grant hazard for free. The
 * holder is `steward-firm-staff` alone (the batch coordinator's decision on this branch,
 * 2026-09-16, after the founder's rulings on steward-app#1426 left the role open): see that
 * role's "The second string" section.
 *
 * Its own `onboarding:` namespace for the reason `engagement:` has one: `matchCapability`
 * expands a verb wildcard inside a resource segment, so a spelling under `ws:` / `mem:` /
 * `inst:` would be swallowed by `workspace-admin`'s, `tenant-admin`'s and `steward-admin`'s
 * wildcards. `onboarding:` has no wildcard holder anywhere but `platform-admin`'s `*:*`.
 */
export const ONBOARDING_REQUEST_CAPABILITY = 'onboarding:request';

/**
 * The `writeCapability` of the `onboarding-request` type (steward-app#1426), and a string NO
 * role in either pack holds, on purpose: it closes appcore's generic entity plane to every
 * family and firm principal (a `*:*` holder aside), while the firm's write goes through the
 * engagement route, which does not consult `writeCapability`. The type's docblock in
 * `index.ts` gives the reasoning and the one residual.
 *
 * ⚠️ **Granting this to any role reopens the forgery it exists to close.** A family role holding
 * it could move `lastIssuedAt`, and so the "requested" rung, on a firm's request; a firm-side
 * role holding it could edit another firm's. `onboardingRequest.test.ts` asserts no holder.
 *
 * Its own `onboarding-request:` namespace, distinct from `onboarding:`, so that no future
 * `onboarding:*` grant (a wildcard `matchCapability` expands within its segment) can reach it.
 */
export const ONBOARDING_REQUEST_WRITE_CAPABILITY = 'onboarding-request:write';

/**
 * The `writeCapability` of the `advisor-client` type (crossmarc/steward-app#1781), and a string
 * NO role in either pack holds, on purpose. While the PACK definition is the one a workspace
 * resolves, it closes appcore's generic MEMBER-plane entity routes
 * (`/w/{ws}/entities/advisor-client[/{id}]`, appcore `router.ts`) to every principal but a
 * `*:*` holder. The intake writer's `/system/*` create is unaffected: that plane runs no
 * `access.ts` gate, so it does not consult this string. appcore enforces `writeCapability` only in
 * `services/platform-api/src/access.ts` (`requiredCapabilities`), and
 * `handlers/system-entities.ts` does not import that module (both read at appcore `aad99f8`,
 * 2026-09-25).
 *
 * ⚠️ **Not "only the writer can write".** A workspace-local CTYPE row always wins over the pack
 * row (appcore `custom-type-resolver.ts`), and `steward-admin` holds `type:write`. So a firm's
 * `steward-admin` can shadow `advisor-client` in the firm's own workspace with a definition that
 * drops this declaration, and then write rows through the generic routes again. That residual is
 * crossmarc/appcore#313 (open on 2026-09-25), and this string does not close it. Nothing here
 * crosses tenants: the shadow and the writes stay in that firm's workspace.
 *
 * The prior art is {@link ONBOARDING_REQUEST_WRITE_CAPABILITY}, with one difference: that type
 * is written through appcore's engagement route, and this one only through `/system/*`.
 *
 * ⚠️ **Granting this to any role reopens the forgery it exists to close.** Under
 * `steward:write` (the declaration before #1781) any firm member holding it could create an
 * `advisor-client` row under an id of their choosing, rewrite a row's `emailKey`, or delete a
 * row. The last one is the worst: the generic delete removes only the head row, so a deleted
 * record's sealed identity (`CLIENTIDENT#`) and custody (`CLIENTASSIGN#`) rows survive. (Until
 * 2026-10-07 the id was derived from the address, so the next invite for it re-created the SAME
 * id and re-attached them; since the id is minted, a re-invite gets a new id and they stay
 * orphaned under the old one. Either way the generic delete is not a client delete.)
 * `advisorClient.test.ts` asserts that no pack role holds it, and that the type declares it.
 * Those are the two things it proves; it proves nothing about a shadowed definition.
 *
 * Its own `advisor-client:` namespace so that no wildcard held today (`ws:*`, `mem:*`,
 * `inst:*`, `steward:*` and the rest) can reach it. Listed in
 * {@link SYSTEM_ONLY_WRITE_CAPABILITIES}, which is what exempts it from the release's
 * "armed capability with no declared holder" check.
 */
export const ADVISOR_CLIENT_WRITE_CAPABILITY = 'advisor-client:write';

/**
 * Write capabilities that are DELIBERATELY held by no role, because every type arming them is
 * designed to be written only by a background actor on appcore's `/system/*` plane, or by
 * appcore's own grant-gated onboarding routes (`onboarding-details`, crossmarc/steward-app#1765);
 * neither consults `writeCapability` (crossmarc/steward-app#1781). "Designed to be", not "can only
 * be": see the crossmarc/appcore#313 residual on {@link ADVISOR_CLIENT_WRITE_CAPABILITY}.
 *
 * Read by exactly one rule: `scripts/lib/packArming.ts` exempts a WRITE capability listed here
 * from `armed-capability-has-no-declared-holder`, which otherwise blocks a release that arms a
 * string no role grants (normally a deny-all accident). Listing a string here is the statement
 * that the deny-all is the design. It never exempts a READ capability. `advisorClient.test.ts`
 * pins that no role holds any string in this list, so the exemption cannot quietly cover a
 * capability somebody later granted.
 *
 * `onboarding-request:write` is NOT here: its exemption is the separate `engagementWrite` one,
 * because its writer is appcore's engagement route rather than a system actor.
 */
export const SYSTEM_ONLY_WRITE_CAPABILITIES: readonly string[] = [
  ADVISOR_CLIENT_WRITE_CAPABILITY,
  // crossmarc/steward-app#1765 (below): no member-plane writer by design.
  'onboarding-details:write',
  'onboarding-prefill:write',
  // crossmarc/steward-app#1889: the three `firm-nw-*` types, written by the nightly firm net
  // worth job over `/system/*` only (`firmNetWorthAccess.ts`).
  FIRM_NW_WRITE_CAPABILITY,
  // steward-app#2581 (docs/39 §8.2): `transaction-cursor`, account-sync's Plaid cursor per
  // connection, written over `/system/*` only.
  CASH_FLOW_CURSOR_WRITE_CAPABILITY,
  // crossmarc/steward-app#2699 (docs/43 §3.2, §6.1): the five client-record types, written by
  // the intake writer over `/system/*`, appcore's client route and appcore's staff route
  // (slice 8); the client route is not over `/system/*` (`clientRecordAccess.ts`).
  ...CLIENT_RECORD_WRITE_CAPABILITIES,
];

/**
 * `onboarding-details` (crossmarc/steward-app#1765, design v2 grant 6): held by NO role. The
 * rows are written only by appcore's grant-gated `/me/onboarding-forms/...` routes, which run
 * no capability check (their authority is the client's onboarding signup grant), and never by
 * firm staff: a firm member who could write here could forge what the client said. Same
 * `#313` shadow residual as {@link ADVISOR_CLIENT_WRITE_CAPABILITY}; appcore's routes refuse a
 * shadowed definition outright (`ONBOARDING_TYPE_SHADOWED`). Listed in
 * {@link SYSTEM_ONLY_WRITE_CAPABILITIES}, whose docblock is widened to say "a background actor
 * OR appcore's own onboarding routes".
 */
export const ONBOARDING_DETAILS_WRITE_CAPABILITY = 'onboarding-details:write';
/** `onboarding-prefill`'s write: the intake writer over `/system/*` only. Held by no role. */
export const ONBOARDING_PREFILL_WRITE_CAPABILITY = 'onboarding-prefill:write';
/**
 * ⚠️ **No `onboarding-prefill:read`, although design v2 grant 6 lists one.** A read capability
 * held by no role is exactly what `scripts/lib/packArming.ts`'s blocking
 * `armed-capability-has-no-declared-holder` refuses, and that rule's system-only exemption is
 * deliberately write-only. Declaring it would need that release guard widened, which is not
 * among the approved grants (crossmarc/steward-app#1765, open question raised 2026-09-25). So
 * the type declares no read capability for now. What that leaves: a firm member could read a
 * prefill row through appcore's generic entity read IF they could reach appcore's member plane,
 * which is SigV4-only (the façade relays only its own named routes, and none names this type).
 * The same reach would already read the underlying `intake-draft` row, which also declares no
 * read capability.
 */

/**
 * The capability appcore's SPONSORED CLIENT SETUP gate asks for (crossmarc/appcore#688 /
 * #570, steward-app#1481), spelled here as a literal for the reason
 * `ENGAGEMENT_ACTIVATE_CAPABILITY` is: this package does not depend on `@appcore/core`.
 *
 * **appcore is the source of truth for this string.** It declares
 * `ENGAGEMENT_PROVISION_CAPABILITY = 'engagement:provision'` in
 * `packages/core/src/db/engagement-repository.ts` (read on appcore's
 * `feat/570-sponsored-setup-repository` branch, 2026-09-16, before it merged), and its
 * `mayProvisionSponsoredSetup` resolves it ONLY from the roles on the caller's membership in
 * the firm record's own workspace (`CFG#advisor-firm.firmWorkspaceId`), never from a role held
 * on any other workspace. The two repos share no package, so a rename on either side is a
 * silent disarm on the other; `packRoles.test.ts` pins the literal. The failure mode of a typo
 * is a gate nobody clears (fail closed), not one everybody clears.
 *
 * Its own `engagement:` namespace for the reason the other two engagement strings have it:
 * `matchCapability` expands a verb wildcard inside a resource segment, so `ws:provision` /
 * `tenant:provision` would be swallowed by `workspace-admin`'s and `tenant-admin`'s `ws:*` /
 * `tenant:*` and handed to every founder. `engagement:` has no wildcard holder anywhere but
 * `platform-admin`'s `*:*`. The holder is `steward-firm-provision` alone; see that role's
 * section at the bottom of this file.
 */
export const ENGAGEMENT_PROVISION_CAPABILITY = 'engagement:provision';

/** The date the firm-authority role was first minted (steward-app#1021). */
const FIRM_AUTHORITY_SEED_DATE = '2026-09-01T00:00:00Z';

/** The date the firm-staff role was first minted (crossmarc/appcore#501). */
const FIRM_STAFF_SEED_DATE = '2026-09-01T00:00:00Z';

/** The date the firm-roster role was first minted (crossmarc/appcore#645). */
const FIRM_ROSTER_SEED_DATE = '2026-09-10T00:00:00Z';

/** The date the firm-provision role was first minted (steward-app#1481). */
const FIRM_PROVISION_SEED_DATE = '2026-09-16T00:00:00Z';

/**
 * The **steward-advisor-firm** App Pack's ROLE facet — four roles since steward-app#1481.
 * This docblock is about the FIRST, `steward-firm-authority`; the second,
 * `steward-firm-staff`, the third, `steward-firm-roster`, and the fourth,
 * `steward-firm-provision`, have their own sections at the end of it, immediately above the
 * array.
 *
 * ## What this role is
 *
 * The single declared holder of appcore's `engagement:activate` capability. That capability
 * is the second half of `mayActivateForFirm` (appcore
 * `packages/core/src/db/engagement-repository.ts`): flipping a `pending` engagement pair to
 * `active` — putting a household onto a firm's roster, with the billing and disclosure
 * consequences that carries — requires an **active membership in the named firm's own
 * tenant** AND a role granting this string, PROVIDED the accepter takes that path.
 *
 * ⚠️ **`mayActivateForFirm` is no longer the only path, since crossmarc/appcore#656 (founder
 * ruling 2026-09-11, "a listed advisor's invite acceptance activates the engagement") — and
 * it is a FALLBACK, not a second independent check tried alongside this one** (corrected
 * steward-app#1386 round 3, item A; the previous draft of this paragraph read as though both
 * gates were evaluated together). appcore's `engagementActivationFor`
 * (`invite-repository.ts`) tries `mayActivateForFirm` first and consults the second gate,
 * `mayActivateAsListedAdvisor`, ONLY WHEN IT REFUSES. That second gate activates the SAME pair
 * for an accepter who holds neither this role nor `engagement:activate` at all: the invite's
 * own listed advisor, matched by email against the firm's OWN advisor-firm record (which the
 * gate reads by the invite's `firmTenantId`, and which must itself be active and list that
 * email on a `listed: true` entry), with an active membership in exactly the firm's workspace
 * carrying `inst:write` (`LISTED_ADVISOR_WRITE_CAPABILITY`) instead.
 *
 * ⚠️ **The active advisor-firm record is that second gate's OWN condition, not a condition of
 * activation generally** (corrected steward-app#1386 round 3, item A). The firm-authority
 * path (`mayActivateForFirm`) never reads an advisor-firm record at all; an accepter who
 * clears it activates the pair without one existing.
 *
 * ⚠️ **Clearing either gate is NECESSARY, not sufficient** (corrected steward-app#1386 round
 * 2, item 5 — this docblock previously said "either gate clearing activates", which
 * overclaimed). `engagementActivationFor` also requires, regardless of which gate cleared: a
 * COMPLETE pending pair on both sides (`EngagementRepository.readPair` — a half-written or
 * TTL-reaped pair refuses), the invite's workspace not being the firm's own tenant (SSR-001),
 * and the transaction's own commit guards — the listed path adds two more of its own, checked
 * at COMMIT rather than at read (the roster slot still listed, and the firm-workspace
 * membership still active at the same `_v`), either of which cancels the whole accept with a
 * 409 `TRANSACTION_CANCELLED` rather than silently joining without activating. Neither gate
 * clearing, or failing any of those, leaves the pair `pending`. This role remains the single
 * holder of `engagement:activate` specifically — that sentence is still true — but
 * `engagement:activate` is no longer the only way into `active`, so a reader must not take
 * "requires a role granting this string" as a claim about every activation. See appcore
 * `invite-repository.ts`'s `engagementActivationFor` for the full gate.
 *
 * It holds `engagement:activate` and **nothing else**, so if it ever lands somewhere
 * nobody intended it grants no read, no write, and no member management. That is the
 * whole role.
 *
 * ## Why `scope: 'workspace'` — REVERSED on 2026-09-09, and this section is a rewrite
 *
 * ⚠️ **This section used to argue the exact opposite, in detail, and it was persuasive.**
 * It said the role took `scope: 'tenant'` so appcore's `assertAssignableRoles` would refuse
 * every self-service assignment path, that "a firm admin could hand themselves firm
 * authority" if it were workspace-scoped, and that "the refusal is the feature". Matt
 * reversed that on 2026-09-09 (steward-app#1343, ruling 2). The old text is in git history;
 * it is not patched here, because a docblock arguing both sides is worse than either.
 *
 * ### The product reason
 *
 * A family's advisory relationship is with a PERSON, not with a firm (#1343). The act that
 * creates it is a firm administrator listing someone as one of the firm's advisors. Listing
 * someone as an advisor **is** handing them the power to accept clients: there is no
 * meaningful version of "you are one of our advisors" that withholds it. The operator vets
 * the firm and its administrator; the administrator vets their own people. Routing that
 * second decision through an operator writing a DynamoDB row by hand is how a control gets
 * routed around rather than used, and it is what the #1114 ceremony had to do after every
 * acceptance.
 *
 * ### What appcore does with the scope, mechanically
 *
 * `assertAssignableRoles` (appcore `packages/core/src/auth/permissions.ts`) refuses any role
 * whose scope is not exactly `'workspace'`, on both the ADDED and the REMOVED edge, at four
 * self-service sites: membership create, membership updateRoles, invite create, invite
 * accept. `'workspace'` is therefore not a preference here; it is the ONLY value at which
 * any of those four paths can carry the role at all.
 *
 * ### WHAT IS BEING GIVEN UP — say it plainly
 *
 * The assignable population is **any `mem:manage` holder in any workspace of the firm tenant
 * that mounts `steward-advisor-firm`**. As read from this file and appcore on 2026-09-09,
 * that set is the firm's own administrators:
 *
 * - `steward-admin` carries literal `mem:manage` (this file, the ADMIN role's `permissions`);
 * - appcore's platform `workspace-admin` carries it (appcore `packages/core/src/entities/role.ts`);
 * - appcore's platform `tenant-admin` reaches it through `mem:*` (same file).
 *
 * And, load-bearing, the roles that do NOT reach it: `steward-member` and `steward-advisor`
 * carry `mem:read` only (this file), and `steward-firm-staff` (below) carries no `mem:`
 * capability at all — only `engagement:read`. So an ordinary advisor or paraplanner at a
 * firm cannot hand this role to anybody, including themselves. That census is a
 * point-in-time reading of two source files on 2026-09-09, not an invariant;
 * `packRoles.test.ts` pins the steward-app half of it so it cannot drift silently, and the
 * appcore half is appcore's to keep.
 *
 * ⚠️ **A firm admin can put this role on their OWN membership, in one PUT.** appcore's
 * `MembershipRepository.updateRoles` takes `userId` straight off the path parameter with no
 * self-target exclusion, and measures the ADD against the CALLER's roles (appcore
 * `packages/core/src/db/membership-repository.ts`, the "SITE 2 OF 4" comment). So "the admin
 * may grant firm authority but may not hold it" is not a boundary that exists, and nothing
 * downstream should be designed as though it were. It is also not a distinction worth
 * wanting: a firm admin who does not hold it cannot clear a pending engagement at all
 * (steward-app#1345), and the remedy is to hold it, not to be forbidden it.
 *
 * ⚠️ **Granting this role by DEFAULT on a firm administrator's invite IS now the rule**
 * (founder ruling, 2026-09-09, recorded on steward-app#1343). This paragraph used to record
 * the opposite: the default was proposed during this change and rejected the same day. The
 * ruling reversed that on the merits, and two of the rejection's three arguments did not
 * hold. "A default grant leaves no act to attribute" is false: the admin invite is issued by
 * an operator, and appcore's `INVITE_ISSUE` records `roleIds` with the issuer. "It makes an
 * admin who is not an advisor unrepresentable" holds only if the People page derives Advisor
 * from `roleIds`; it must derive it from the roster's `listed` flag instead.
 *
 * A rule is not a mechanism, so which invite paths actually APPLY the default, as of
 * 2026-09-10: appcore's `seed-advisor-firm` (binds `[steward-admin, steward-firm-staff,
 * steward-firm-authority]`, pinned by its own test), and "Your firm"'s Add a person
 * (steward-app#1361), which ticks the firm roles when Admin is chosen and can be unticked
 * (since crossmarc/appcore#645 that is all three, `steward-firm-roster` included; appcore's
 * `seed-advisor-firm` does not yet bind the third, which is appcore's to change).
 * crossmarc-ops#147's one-form firm creation is meant to be a third, through a
 * provisioning-only path; until it ships, the ops console's manual operator invite
 * (`core/src/firm-members.ts`) deliberately cannot carry `steward-firm-authority`. Any other
 * path, and every admin invited before the ruling, gets whatever it was given.
 * That page labels by role and shows no Advisor label at all, because `listed` is not yet
 * readable per person (crossmarc/appcore#645).
 *
 * ### Who this actually reaches today — a dated reading, not an invariant
 *
 * Drained scans of all three environment tables in `us-west-2` on 2026-09-09, run by this
 * change's coordinator (not by this file's author), each terminating on an absent
 * `LastEvaluatedKey` and each with a positive control: **every firm-tenant membership in dev
 * (2/2), stage (1/1) and prod (3/3) carries `steward-admin`**, and five of those six already
 * hold `steward-firm-authority` outright. So on the day this ships it grants nothing to
 * anybody who is not already a firm administrator, and it lands close to a no-op.
 *
 * ⚠️ **That is a census and a census expires — this one expires by design.** It stops being
 * true the moment #1343's "Add a person" mints a second membership, and the whole safety
 * argument above rests on that future membership being `steward-member` rather than
 * `steward-admin`. Re-read at source before leaning on it. (It supersedes the figures in
 * steward-app#1245, which say 1/1/1; those are stale, do not cite them.)
 *
 * ### What still protects a CLIENT (family) workspace — the mount, not the scope
 *
 * A client workspace mounts `steward-app` only (`services/steward-app-api/src/handlers/
 * workspaces.ts` and `onboarding.ts` both pass `packs: [steward-app]`). `loadRole` resolves a
 * pack role by walking the workspace's mounted packs, so in a client workspace this roleId
 * resolves to nothing and `assertAssignableRoles` refuses it — 403 `ROLE_NOT_ASSIGNABLE` —
 * whatever its scope says. That is what stops a family administrator granting themselves
 * firm authority in their own workspace, and it is why the scope change does not reach them.
 * `client-workspace-pack-mounts.test.ts` (steward-app-api) pins that no client-side workspace
 * creation path mounts this pack.
 *
 * ⚠️ **Two honest limits on that barrier, both worth knowing before leaning on it.**
 *
 * 1. **`loadRole`'s cache is keyed by roleId ALONE and consults `packs` only on a miss**
 *    (appcore `auth/permissions.ts`, 60s TTL). A role resolved for ANY workspace in the last
 *    minute resolves in a workspace that does not mount its pack too. appcore's own docblock
 *    calls this out and `readRoleFresh` exists because the elevation route would not tolerate
 *    it. So the mount barrier is reliable cold and a coin flip warm, for up to 60 seconds.
 *    Cold it refuses; warm it may permit. That is a real narrowing of the guarantee this
 *    section can claim, and it is the reason the next paragraph matters.
 * 2. **A client workspace inside a FIRM's tenant would defeat it** if that workspace ever
 *    mounted this pack. appcore's `services/platform-api/src/handlers/engagements.ts` records
 *    that firm-side client provisioning (docs/30 T2) mints the household's own founder
 *    `workspace-admin` there, which carries `mem:manage`. Founder + pack mount + this scope =
 *    that founder self-granting firm authority in the firm's tenant, which is both halves of
 *    `mayActivateForFirm`. The property that keeps this unsafe combination unreachable is
 *    that no client-side workspace mounts `steward-advisor-firm`; the test named above is
 *    what holds it, and it is phrased as that property rather than as a project, because the
 *    day some other workstream delivers firm-side provisioning is the day a trigger written
 *    as a date goes quiet.
 *
 *    ⚠️ **That test holds the property only for mounts THIS REPO writes, and an earlier
 *    revision of this paragraph overclaimed by saying part 2 "is the one this repo
 *    controls".** appcore's operator plane mounts packs too. **crossmarc/appcore#626** is the
 *    worked example: a `provision:workspace` holder can mint a workspace mounting
 *    `steward-advisor-firm` inside a CUSTOMER's tenant and self-invite as `workspace-admin`,
 *    and no steward-app source sweep can see it. Related: crossmarc/appcore#637 records nine
 *    dev workspaces mounting this pack whose tenant has no advisor-firm record at all (stage
 *    and prod clean; coordinator's drained scan, 2026-09-09), so the tidier invariant "only
 *    real firm workspaces mount the pack" is already false in an environment.
 *
 *    ⚠️ **And this change narrows #626's own stated bound, which is why it is cross-linked
 *    here rather than only in that test.** Before the reversal, decorating such a membership
 *    with firm authority needed `role:elevate` — a SECOND, separate operator capability, and
 *    #626 names that two-capability requirement as its bound. Afterwards `workspace-admin`'s
 *    literal `mem:manage` is enough, and the role resolves COLD from the pack partition of a
 *    workspace that really does mount the pack, so no cache window is needed either. The
 *    actor is an operator throughout, so this is privilege chaining rather than a
 *    customer-reachable escalation and it was not treated as blocking; #626's own third
 *    remediation option is "restore the two-capability property", and this paragraph is the
 *    record that the option got cheaper to need. Raised by this branch's security review,
 *    2026-09-09.
 *
 * ### The rest of `mayActivateForFirm` still bounds the blast radius
 *
 * The capability is only half the gate. The other half is an ACTIVE MEMBERSHIP IN THE NAMED
 * FIRM'S OWN TENANT (`m.tenantId === firmTenantId`), so this role granted anywhere else
 * satisfies nothing — a stray grant in a family tenant activates no engagement for anybody.
 * And the role holds `engagement:activate` and nothing else, so a stray grant reads nothing,
 * writes nothing and manages no members. Both properties are why the reversal is affordable.
 *
 * ### Two things the scope change does NOT do
 *
 * - **It is inert until each environment's pack row is re-seeded.** Scope is read from the
 *   persisted `APP#steward-advisor-firm` / `ROLE#…` row, never from this declaration
 *   (appcore `auth/permissions.ts`). `scripts/lib/packSeed.ts` fingerprints every
 *   non-provenance field, so a scope-only diff rewrites the row — the seed script is the
 *   arming act. Add the 60-second role cache after that. Until the seed runs in an
 *   environment, every self-service assignment there still 403s exactly as before.
 * - **It does not let a firm admin who is NOT a listed advisor clear a pending engagement.**
 *   `mayActivateForFirm` still needs `engagement:activate`, and after this change an admin who
 *   holds only `[steward-admin, steward-firm-staff]` still does not carry it. The founder
 *   ruling of 2026-09-09 settled the remedy as a ROLE, not a scope change: the firm admin's
 *   invite carries this role by default, and "Your firm" (steward-app#1361, 2026-09-10) lets
 *   an admin tick "Accepts clients" on anyone, themselves included, and then Accept a waiting
 *   family through the existing `POST /advisor/pending/{workspaceId}/activate`. An admin who
 *   was invited before the ruling and never ticks it still cannot activate. Do not read the
 *   scope reversal as having closed that; the role grant is what closes it.
 * - **It does not fix appcore's operator route.** `assertElevatedRoleAssignable` refuses an
 *   allowlisted id whose role is not `scope: 'tenant'`, while `assertElevatedRoleRemovable`
 *   checks the allowlist alone. So until `steward-firm-authority`'s id comes OUT of
 *   `ASSIGNABLE_ELEVATED_ROLE_IDS`, the operator route can REMOVE the role and can no longer
 *   GRANT it. That is an appcore change in an appcore PR; it is named here because this is
 *   the file whose edit creates the asymmetry.
 *
 * ## ⚠️ Where a MIS-landed grant still hurts
 *
 * **Compose it ONTO a membership in the FIRM'S OWN TENANT that already carries the
 * principal's ordinary roles. Never as a lone role, and never on a client workspace's
 * membership.** The scope reversal changes who may do the composing; it changes none of the
 * reasons below.
 *
 * - **Alone, it gives its holder nothing — and the reason first written here was wrong.**
 *   That reason said the role is absent from the shared no-write list "deliberately,
 *   because `engagement:activate` is not a read", and concluded that a lone grant would
 *   fail OPEN through `isReadOnlyRoleSet` into `writeGate.ts`'s legacy fallback. The
 *   omission was a BUG, not a decision (steward-app#1102). That list's predicate is not
 *   "is a read"; it is **confers no `inst:write`** — the capability `writeGate.ts` pins as
 *   `WRITE_CAPABILITY` and the one the fallback exists to approximate. This role's
 *   permission set is exactly `[ENGAGEMENT_ACTIVATE_CAPABILITY]`, and appcore's
 *   `matchCapability` expands only exact match, `*:*` and `<prefix>:*`, so
 *   `engagement:activate` cannot reach `inst:write` under any of the three. The role is now
 *   listed in `NO_WRITE_ROLE_IDS` (`roles.ts`), so the fail-open no longer applies to it:
 *   a lone grant is REFUSED by that fallback rather than trusted by it, and the real reason
 *   to compose rather than grant alone is the plainer one — a membership carrying only this
 *   role can read nothing in the tenant it is meant to act for.
 * - **On a client workspace's membership it breaks the sharing controls, and the mechanism
 *   moved with the scope.** It used to be the assignability refusal: `PUT /advisor/
 *   shared-areas` re-submits the member's whole `roleIds` array, so appcore 403'd the
 *   family's own area change. The refusal is still there, but it now comes from the PACK
 *   MOUNT rather than from the scope — a client workspace does not mount
 *   `steward-advisor-firm`, so the id resolves to nothing and both edges refuse — which per
 *   the cache note above is reliable cold and a coin flip warm. A refusal that depends on
 *   Lambda warmth is not something to build on, so `UNASSIGNABLE_ROLE_IDS` (`roles.ts`)
 *   keeps this id and `connect`'s pin loop keeps skipping such a membership rather than
 *   attempting a whole-array REPLACE over it. Read that list's docblock before removing the
 *   entry: dropping it would turn that skip into a warm/cold coin-flip 403.
 * - **The firm tenant is where it is needed anyway.** `mayActivateForFirm` filters
 *   memberships to `m.tenantId === firmTenantId`, so a grant anywhere else satisfies
 *   neither half of the gate.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────
 *
 * ## The pack's SECOND role: `steward-firm-staff` (crossmarc/appcore#501)
 *
 * ⚠️ Everything ABOVE this line is about `steward-firm-authority`; everything below is about
 * `steward-firm-staff`. They are ONE docblock because only the last `/** … *\/` before a
 * symbol attaches to it in TypeScript — two adjacent blocks would silently detach the
 * firm-authority half from the export and hide it from editor hover (caught in this PR's
 * code review).
 *
 * The declared holder of appcore's `engagement:read`, the capability that gates
 * `GET /tenants/{tenantId}/engagements` — the firm's client roster, with each engagement's
 * `floorPolicy` and `payerRef`. Since steward-app#1426 it also holds `onboarding:request`, and
 * nothing else; see "The second string" below.
 *
 * ### Why the read needed its own role at all
 *
 * #501's threat is not a stranger; the route already refuses those. It is **any member of
 * the firm's tenant, at any role** — a contractor, an intern, a viewer membership in an
 * unrelated firm workspace — enumerating the firm's whole client list by calling appcore
 * directly, around the façade. steward-app's own roster intersection (`handlers/advisor.ts`,
 * steward-app#1101) narrows what the PRODUCT shows; it is not a boundary, because the caller
 * does not need the product.
 *
 * That is also why the capability could not be given to the steward-app pack's ADMIN /
 * MEMBER / VIEWER roles: **granting it there would exclude nobody who holds a steward role**,
 * which is everybody the threat model is about. A capability whose holder set is "everyone
 * already inside" is decorative. It only has teeth on a firm-specific role, so this is one.
 *
 * ### Why `scope: 'workspace'` — the SAME scope as firm authority since 2026-09-09
 *
 * ⚠️ **This heading used to read "the OPPOSITE of the firm-authority role above" and the
 * asymmetry it described is gone.** Both roles in this pack are now `'workspace'`. What
 * this role's scope buys is unchanged and is still worth stating: a firm admin adding a new
 * paraplanner grants them the client roster through the ordinary member and invite paths,
 * and the four self-service sites (membership create, membership updateRoles, invite create,
 * invite accept) are exactly the paths that has to work through. A `'tenant'` scope here
 * would mean every new hire needs an operator to write a DynamoDB row.
 *
 * The two roles are still not comparable in blast radius, and that difference now lives in
 * WHAT they grant rather than in who may grant them: the worst a mis-granted
 * `engagement:read` does is show a firm's own client list to somebody the firm already made
 * a member of its own workspace, and it confers no `inst:write` (`NO_WRITE_ROLE_IDS`,
 * `roles.ts`). The worst a mis-granted `engagement:activate` does is bind a household to a
 * firm, with the billing and disclosure consequences that carries. Read the firm-authority
 * half above for why that second one is now a firm administrator's decision to make.
 *
 * ### It does not subsume, and is not subsumed by, firm authority
 *
 * `steward-firm-authority` stays single-purpose (`[engagement:activate]`). A binder who also
 * needs to read the roster holds BOTH roles. Do not "simplify" by adding `engagement:read`
 * to firm authority: the two are separate decisions a firm admin makes about a person, and
 * merging them means every advisor listed as able to accept clients also gets the firm's
 * whole client list with its `floorPolicy` and `payerRef`, whether the firm wanted that or
 * not. (The old reason given here was that firm authority is "deliberately unassignable";
 * that reason expired on 2026-09-09 and the rule did not.)
 *
 * ### The deployment ordering this role exists to make safe
 *
 * appcore's gate and this grant are two artifacts and the grant must land FIRST. If appcore
 * arms `engagement:read` while no firm staff hold this role, the roster returns 403, the
 * façade turns that into a failed request, and the firm's advisor portal shows a firm with no
 * clients. That failure is worse than the enumeration it fixes, which is why #501 was
 * deliberately not folded into appcore#497. `scripts/lib/packArming.ts` (steward-app#965)
 * reports the role as `role-missing` / `role-permissions-differ` against any environment that
 * has not been re-seeded, so the prerequisite is verifiable rather than remembered — but the
 * check sees the pack ROW, not who holds it. **Whether a given environment's firm-staff
 * memberships carry this id is deployed state: verify it in DynamoDB, not here.**
 *
 * ### The second string: `onboarding:request` (steward-app#1426, 2026-09-16)
 *
 * This role carried exactly one string until #1426, under the rule that a role carrying the
 * roster and something else "is a role whose grant stops being a single decision". The rule
 * exists to bound what a grant of this role REACHES. The second string is inside that bound,
 * and the reason is the gate, not the role:
 *
 * - **Only a firm-tenant membership counts.** appcore's engagement-authorized write resolves
 *   the capability through `resolveFirmCapability`
 *   (`packages/core/src/db/engagement-repository.ts`, appcore `19caa7c`), which keeps only
 *   memberships whose `tenantId` equals the NAMED firm's tenant and whose firm workspace is
 *   `active`. A family admin who assigns this role to themselves in their OWN workspace (refused
 *   cold by the pack mount, possible for up to 60s warm, see firm authority's section above)
 *   still fails that gate, because the membership is in the family's tenant and a family cannot
 *   mint a membership in a firm's tenant.
 * - **What the string buys is one row, in a family that already let the firm in.** The route
 *   also requires the caller to be a live member of the family workspace, an `active`
 *   engagement pair between that firm and that family, and it writes only
 *   `ENTITY#onboarding-request#<firmTenantId>` with an id the server derives. It reads nothing
 *   and grants nothing. That is true while `onboarding-request` is the ONLY type declaring
 *   `engagementWrite.firmCapability: 'onboarding:request'` (2026-09-16); a second declaring type
 *   widens what this role reaches and needs this argument re-made.
 * - **Who inside the firm can already self-grant it** is exactly who can self-grant the roster
 *   read today: a `mem:manage` holder in a workspace mounting this pack (crossmarc/appcore#642).
 *   That population did not grow.
 *
 * Why this role and not the other two (the batch coordinator's decision, 2026-09-16): the sender
 * is the firm's client-facing staff, not only the person who may bind a household. Every active firm-workspace membership held this role
 * in drained scans of dev (3/3), stage (1/1) and prod (3/3) run by this change's author on
 * 2026-09-16 18:40Z; only 5 of those 7 held firm authority. That is a census, not an
 * invariant. `engagement:activate` was ruled out for the reasons on
 * `ONBOARDING_REQUEST_CAPABILITY`. Roles resolve from the pack row at request time, so
 * existing memberships need no backfill: the seed is the arming act.
 *
 * `packRoles.test.ts` pins EXACTLY these two strings and that no other role in either pack
 * carries the second.
 *
 * ### ⚠️ Where a MIS-landed grant hurts, which differs from firm authority's case
 *
 * Grant it on a membership in the FIRM's own tenant. On a CLIENT workspace's membership it
 * does the same damage the firm-authority section above describes, and it is easier to land
 * there because ordinary paths can: `PUT /advisor/shared-areas` carries a member's non-pillar
 * roles through verbatim (`handlers/advisor.ts`), and a client workspace does not mount
 * `steward-advisor-firm`, so appcore's `assertAssignableRoles` then 403s
 * `ROLE_NOT_ASSIGNABLE` on the family's own area change. The skip-when-unchanged in that
 * handler masks it until the family next edits a shared area. Availability, not
 * confidentiality: the capability resolves only from firm-tenant memberships, so a stray
 * grant elsewhere reads nothing.
 *
 * This is also the first step toward steward-app#1026's server-managed firm roster: once
 * "firm staff" is a role rather than an inference from a membership, the roster has something
 * to be managed against.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────
 *
 * ## The pack's THIRD role: `steward-firm-roster` (crossmarc/appcore#645)
 *
 * The declared holder of appcore's `firmroster:write`, the capability that gates the
 * member-plane roster writes: `POST /w/{ws}/advisor-roster` (add a person to the firm's public
 * roster) and `PUT /w/{ws}/advisor-roster/{advisorId}` (change their name, title or listed
 * flag). It holds that one string and nothing else.
 *
 * ### What bounds it, and where each bound lives
 *
 * - **The workspace.** appcore answers only when the call's workspace IS the firm's own
 *   workspace (`firmWorkspaceId` on the firm record), so a grant anywhere else writes nothing.
 * - **The person.** Every create must name an invite-accepted member of that workspace; a
 *   stranger's address is refused `ADVISOR_ROSTER_MEMBERSHIP_REQUIRED`. So the worst a
 *   mis-granted `firmroster:write` does is change how the firm's OWN people are presented to
 *   families, and it cannot put an outsider's ADDRESS on the firm's listing. It can put any
 *   NAME on an insider's card: name and title are free text the admin may rewrite, which
 *   the founder accepted for #645.
 * - **The fields.** No delete, no email change, no `intro`, no firm-level field. Those remain
 *   the operator's (the crossmarc-ops console), in appcore's grammar rather than here.
 *
 * ### Why a separate role rather than a string on firm authority
 *
 * Accepting clients and editing the public listing are two decisions a firm admin makes about
 * a person, the same argument that keeps `engagement:read` off firm authority. A firm might
 * let its office manager keep the listing tidy without letting them bind a household.
 *
 * ### Who gets it by default
 *
 * NOT every path that provisions a firm ADMIN grants it. The ones that do and do not, as of
 * this branch (2026-09-10):
 *
 * - **Grants it:** "Your firm"'s Add a person, which ticks it beside `steward-firm-authority`
 *   when Admin is chosen.
 * - **Converges it once:** `scripts/backfill-firm-roster-role.ts`, a one-shot operator run
 *   that grants it to the firm admins present WHEN it runs (firm-record workspaces only). An
 *   admin made afterwards by a path below is not reached until someone runs it again.
 * - **Does not grant it:** Settings -> Members' invite form, which invites an Admin with the
 *   single role id it picks (`roleIds: [inviteRoleId]`); such an admin then needs the "Edits
 *   listing" box ticked. appcore's `seed-advisor-firm` and crossmarc-ops' firm creation, which
 *   are separate repos and are follow-ups, not done.
 *
 * ### Ordering
 *
 * Inert in an environment until `seed-steward-advisor-firm.ts` writes its row there, like the
 * two above: appcore resolves the role from the persisted pack row, and an unseeded id is
 * refused `ROLE_NOT_ASSIGNABLE` on every grant path. So seed first, then run the backfill.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────
 *
 * ## The pack's FOURTH role: `steward-firm-provision` (steward-app#1481, SCS 1.4)
 *
 * The declared holder of appcore's `engagement:provision`, the capability that gates
 * sponsored client setup: a firm principal creating a client's OWN tenant and workspace, with an
 * `active` sponsored engagement, the firm's advisors as members, and a founding invite for the
 * family (crossmarc/appcore#688 for the repository, #570 for the routes). It holds that one
 * string and nothing else.
 *
 * ### Why a role of its own (founder decision 3, steward-app#1016, 2026-09-16)
 *
 * "Any firm admin or advisor" may set up an account, "implemented as its own capability granted
 * to both firm roles, so narrowing it for RIPL later is a role change, not code." Putting the
 * string on `steward-firm-staff` would have delivered the first half and lost the second: a
 * firm that later wants only some staff to set up accounts would need the string moved, which
 * is a code change and a re-seed in every environment. As its own role, narrowing is unticking
 * a box on a person. It also keeps firm staff at the two strings its "second string" argument
 * above was made for.
 *
 * ### Who gets it by default
 *
 * Every INVITE path in this repo that makes someone a firm ADMIN or MEMBER composes it beside
 * `steward-firm-staff`, and the backfill converges the people who are already there. **Never a
 * Viewer** (coordinator ruling applying decision 3, 2026-09-16: a read-only person must not hold
 * a capability that creates a client's account and invites the family). The rule is
 * `mayHoldFirmProvision` in `roles.ts`, and the façade REFUSES a role set carrying this role
 * without an Admin or Member role on `POST /invites`, `POST /invites/{token}/resend`, `POST /members` and `PUT /members`
 * (`firmProvisionWithoutBaseRole`); appcore does not know the rule.
 * `scripts/lib/firmProvisionGrantSites.test.ts` is a census of names, per file: it fails when
 * a file that names firm staff in code does not also name this role, and it cannot see WHICH
 * code path in a file grants what (its own docblock lists what it misses):
 *
 * - "Your firm"'s Add a person (`src/advisor/YourFirm.tsx`, `inviteRoleIds`), which adds the three
 *   default firm roles to every invite and this one to Admin and Member invites only;
 * - the Firm page's People rows (`src/advisor/YourFirm.tsx`, steward-app#1729) carry the same
 *   checkbox as Settings → Members below, through the same `roleIdsAfterFirmRoleToggle` and
 *   `provisionBoxDisabled`; for a firm admin that page is offered to, Members is hidden;
 * - Settings → Members keeps it through an ordinary-role change (`PICKER_PRESERVED_ROLE_IDS`),
 *   except a change to Viewer, which drops it (`roleIdsAfterPick`), and offers its own checkbox,
 *   the revoke half, disabled on a Viewer row that does not hold it. ⚠️ Its "See the client
 *   roster and ask clients for details" checkbox (the firm-staff toggle; relabelled
 *   steward-app#1544, was "Firm staff") is NOT a provision grant: each firm checkbox there adds
 *   or strips only its own id (`roleIdsAfterFirmRoleToggle`), the same as "Accept clients" and
 *   "Edit the firm listing" have never been ticked by it. So a firm admin who ticks the
 *   firm-staff box on an existing member makes a firm person without this role until they also
 *   tick "Set up client accounts", which sits beside it. Unticking the firm-staff box likewise
 *   leaves this role in place. Deliberate: one box moving another is the surprise those
 *   controls were built to avoid;
 * - the ceremony verifier (`scripts/lib/firmCeremony.ts`) expects it on a full-grant advisor;
 * - `scripts/backfill-firm-provision-role.ts` converges the firm-staff holders present when it
 *   runs, skipping and naming Viewer-only ones.
 *
 * Outside this repo, and NOT done by this change: crossmarc-ops' operator invite and firm
 * creation lists (a separate crossmarc-ops PR), and appcore's `seed-advisor-firm`, which on
 * appcore `main` (read 2026-09-16) composes firm staff, firm authority and firm roster but not
 * this role (crossmarc/appcore#691).
 *
 * ### What bounds it, and where each bound lives
 *
 * - **Only the firm record's own workspace counts** (appcore `mayProvisionSponsoredSetup`,
 *   clause (b)+(c), read on appcore's `feat/570-sponsored-setup-repository` branch on
 *   2026-09-16): the roles on the caller's ACTIVE membership in exactly
 *   `CFG#advisor-firm.firmWorkspaceId`, resolved against THAT workspace's packs. The role held
 *   on any other membership, in the firm's tenant or anywhere else, grants nothing.
 * - **A firm record must exist and be active** (clause (a)). A family tenant has none, so a
 *   family admin who somehow held this string could still not name their own tenant as the
 *   firm. appcore calls that clause load-bearing precisely because `steward-admin` is minted to
 *   every family founder.
 * - **It is never on a steward-app (family) pack role**, which is this repo's half of the same
 *   defence: `packRoles.test.ts` walks every family-pack role and asserts none reaches the
 *   string, literally or by wildcard. Clause (a) is the barrier if that test is ever wrong;
 *   the test is the barrier if clause (a) ever is.
 * - **What a holder can do is bounded by appcore's primitive**, not by this role: at most 5
 *   advisors per setup, advisors must be active members of the firm workspace, no admin role
 *   for the firm in the family's workspace, and per-firm and per-user daily counters.
 *
 * ### ⚠️ Who inside the firm can self-grant it
 *
 * The same population as every other role in this pack: a `mem:manage` holder in a workspace
 * mounting `steward-advisor-firm` (crossmarc/appcore#642). A firm admin can therefore always
 * give themselves this role, so "narrow it to some staff" constrains non-admins only. That is
 * decision 3's own shape (admins are in the population anyway), not a gap this role opens.
 *
 * ### Ordering
 *
 * 1. Seed the role row in each environment (`seed-steward-advisor-firm.ts`). Until then every
 *    grant of the id is refused `ROLE_NOT_ASSIGNABLE`, and "Your firm"'s invite carries it, so
 *    that invite would 403. `deploy.yml`'s `seed-pack` job runs this seed when
 *    `packages/steward-types` changed or on a dispatch, which covers this change. It does NOT
 *    fully close the window: `api` refuses to run after a FAILED seed, but `spa` blocks only
 *    on `api` failing or being cancelled, so a failed seed plus a skipped `api` still ships
 *    the SPA (read in deploy.yml, 2026-09-16). After any deploy of this change, confirm the
 *    role row exists (packArming's verify step reports `role-missing`) before relying on the
 *    invite.
 * 2. Backfill existing firm-staff holders (`backfill-firm-provision-role.ts`, dry run first).
 * 3. appcore#570's routes arm the gate. Before step 3 the role is held and inert; after it,
 *    a firm person who was missed by step 2 is refused setup, which is the fail-closed
 *    direction. Whether an environment's memberships carry the id is deployed state: read it
 *    in DynamoDB, not here.
 */
export const STEWARD_ADVISOR_FIRM_ROLE_DEFS: readonly PackRoleDef[] = [
  {
    roleId: STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_AUTHORITY,
    code: 'steward-firm-authority',
    name: 'Firm Authority',
    // 'workspace', REVERSED from 'tenant' on 2026-09-09 (steward-app#1343 ruling 2). This is
    // the value that lets appcore's `assertAssignableRoles` carry the role at all, on the four
    // self-service sites; every other value refuses it everywhere. Read this file's
    // `steward-firm-authority` section above BEFORE changing it: the section names who can
    // now assign it, what that gives up, and the fact that the pack mount rather than the
    // scope is what keeps it out of a family's own workspace. The change is inert in an
    // environment until `seed-steward-advisor-firm.ts` rewrites the row.
    scope: 'workspace',
    description: 'Lets a principal accept a household onto the firm\'s roster on the firm\'s behalf. A firm administrator grants it to the people they list as advisors.',
    // EXACTLY one string. Do not add to this list: a role that carries firm authority and
    // also carries reads is a role somebody will want to hand out.
    permissions: [ENGAGEMENT_ACTIVATE_CAPABILITY],
    builtIn: true,
    createdAt: FIRM_AUTHORITY_SEED_DATE,
  },
  {
    roleId: STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_STAFF,
    code: 'steward-firm-staff',
    name: 'Firm Staff',
    // 'workspace', the same scope the role above now takes (it was 'tenant' until
    // steward-app#1343 on 2026-09-09). See the docblock: this one is meant to be grantable by
    // a firm's own admin without an operator, so it has to pass appcore's
    // `assertAssignableRoles`. ⚠️ The GRANT PATH TODAY is the member-role update
    // (`PUT /members/{userId}`), not the SPA's Members picker: that picker reads one global
    // `ROLE_OPTIONS` list with no notion of which workspace is open, so this role is
    // deliberately absent from it (`src/api/types.ts` says why). The scope is what makes the
    // API path work; the picker is a separate, unfinished piece.
    scope: 'workspace',
    // ⚠️ The description is read in appcore's role lists. "Confers no write of any kind" was
    // true until steward-app#1426 and is not now: it also sends a connected family the firm's
    // onboarding request. It still confers no `inst:write` (`NO_WRITE_ROLE_IDS`).
    description: 'Lets a member of the firm see the firm\'s client roster and its terms, and send a connected family the firm\'s onboarding request. Grant it to staff who work with clients; it does not let its holder place a household on the roster.',
    // EXACTLY TWO strings since steward-app#1426 (it was exactly one). The one-string rule
    // exists to bound what a grant of this role reaches; see "The second string" in the
    // docblock above for why `onboarding:request` stays inside that bound. Firm authority and
    // firm roster still carry exactly one each.
    permissions: [ENGAGEMENT_READ_CAPABILITY, ONBOARDING_REQUEST_CAPABILITY],
    builtIn: true,
    createdAt: FIRM_STAFF_SEED_DATE,
  },
  {
    roleId: STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_ROSTER,
    code: 'steward-firm-roster',
    name: 'Firm Roster',
    // 'workspace', like both roles above: a firm admin grants it through the ordinary member
    // and invite paths, so it has to pass appcore's `assertAssignableRoles`.
    scope: 'workspace',
    description: 'Lets a member of the firm keep the firm\'s advisor listing up to date: add one of the firm\'s own people, and change their name, title, or whether families can see them.',
    // EXACTLY one string, for the reason firm authority carries one. (Firm staff above carries
    // two since steward-app#1426; its docblock section "The second string" says why.)
    permissions: [FIRM_ROSTER_WRITE_CAPABILITY],
    builtIn: true,
    createdAt: FIRM_ROSTER_SEED_DATE,
  },
  {
    roleId: STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_PROVISION,
    code: 'steward-firm-provision',
    name: 'Firm Client Setup',
    // 'workspace', like the three roles above: a firm admin grants and revokes it through the
    // ordinary member and invite paths, which is what makes decision 3's "narrow it later by a
    // role change" possible at all.
    scope: 'workspace',
    description: 'Lets a member of the firm set up a Steward account for a client, which the firm sponsors. A firm administrator grants it to the firm\'s admins and advisors, and can take it back.',
    // EXACTLY one string. It is a separate role precisely so it can be withheld from someone who
    // keeps every other firm role; a second string here would make that withholding take
    // something else with it.
    permissions: [ENGAGEMENT_PROVISION_CAPABILITY],
    builtIn: true,
    createdAt: FIRM_PROVISION_SEED_DATE,
  },
];

/**
 * Every built-in role definition across both packs this façade knows about. The census
 * `rolesGrantingCapability` below (and any future whole-fleet check built on it) scans
 * this and only this — a pack added later that is not folded in here silently escapes
 * every check derived from it, so add a new pack's defs in the SAME commit that creates
 * it, not a follow-up. `packRoles.test.ts` keeps its own copy of this same union (predates
 * this export) for the reason its own comment there gives: a test asserting through the
 * thing it is meant to catch drift in is weaker than one built independently of it.
 */
export const ALL_PACK_ROLES: readonly PackRoleDef[] = [
  ...STEWARD_APP_ROLE_DEFS,
  ...STEWARD_ADVISOR_FIRM_ROLE_DEFS,
];

/**
 * The `roleId`s of every built-in role, across both packs, whose `permissions` include
 * `capability` — in {@link ALL_PACK_ROLES}'s own order, deduplicated by construction (a
 * `roleId` is unique to one def). Mechanical, not maintained: a role gaining or losing a
 * capability changes this function's answer without anyone updating a second list.
 *
 * **Never hand-list a capability's holders.** `POST /advisor/intake-link/email`'s
 * authorization check (steward-app#1543 part a follow-up, M1 security review 2026-09-18)
 * is the first caller — before it, the façade let the `?firmId=`-narrowing `advisorFirmContext`
 * stand in for an authorization gate, which its own docblock has always called "UX gating
 * only — never authorization" (`advisorFirms.ts`, `firmForTenant`). A literal array of role
 * ids at the call site would silently stop tracking `ONBOARDING_REQUEST_CAPABILITY`'s own
 * carriers the first time a role's `permissions` changed elsewhere in this file; this
 * derivation cannot drift from the defs because it reads them.
 */
export function rolesGrantingCapability(capability: string): readonly string[] {
  return ALL_PACK_ROLES.filter(r => r.permissions.includes(capability)).map(r => r.roleId);
}

/**
 * `rolesGrantingCapability(ONBOARDING_REQUEST_CAPABILITY)`, precomputed once at module load
 * for every caller that just needs the set (not the general function). As of this file's
 * own `steward-app#1426` tripwire section above and `packRoles.test.ts`'s corresponding
 * test, this is exactly one role id: `STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_STAFF`
 * (`steward-firm-staff`). That count is a fact this array reads at runtime, not a number
 * copied into a comment — if a second role ever declares the capability, this constant
 * widens with it and the test that pins "exactly one holder" is what goes red, not this
 * file.
 */
export const ONBOARDING_REQUEST_ROLE_IDS: readonly string[] = rolesGrantingCapability(ONBOARDING_REQUEST_CAPABILITY);
